import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ExpiryField, { expiryInputFromIso, expiryInputToIso } from '@/features/vault/ExpiryField';
import { credentialsApi, type CredentialDetail } from '@/api/credentials';
import { type CredentialTypeSchemaResponse } from '@/api/credentialSchemas';
import { errorMessage } from '@/api/client';
import { tenantsApi } from '@/api/tenants';
import { base64ToBytes, encryptCredentialForShares, type CredentialPayload } from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import { CredentialFormFields, initialFieldsFromSchema, partitionValues } from '@/features/vault/CredentialForm';

// ==========================================================
// EditForm — same look ca CreateForm, pre-populat + PATCH + new DEK
// ==========================================================

interface EditFormProps {
  cred: CredentialDetail;
  schema: CredentialTypeSchemaResponse;
  initialPlaintext: Record<string, unknown>;
  onCancel: () => void;
  onSaved: () => void;
}

export function EditForm({
  cred,
  schema,
  initialPlaintext,
  onCancel,
  onSaved,
}: EditFormProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(cred.title);
  const [changeReason, setChangeReason] = useState('');
  const [expires, setExpires] = useState(() => expiryInputFromIso(cred.expires_at));
  const [values, setValues] = useState<Record<string, string>>(() =>
    initialFieldsFromSchema(schema, cred.meta, initialPlaintext),
  );
  const [error, setError] = useState<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const currentTenantId = useAuthStore((s) => s.currentTenantId);

  const mutation = useMutation({
    mutationFn: async () => {
      const { plaintext, meta } = partitionValues(schema, values);
      if (!currentTenantId) {
        throw new Error(t('vault_ui.detail.no_space_selected'));
      }

      // Fetch shares active + members tenant pentru a lua public_key-urile.
      const [shares, members] = await Promise.all([
        credentialsApi.listShares(cred.id),
        tenantsApi.listMembers(currentTenantId),
      ]);

      const recipients = shares.map((s) => {
        const m = members.find((mm) => mm.user_id === s.user_id);
        if (!m) {
          throw new Error(
            t('vault_ui.detail.share_user_without_member', { userId: s.user_id }),
          );
        }
        return {
          userId: s.user_id,
          publicKey: base64ToBytes(m.public_key),
          isSelf: s.user_id === user?.id,
        };
      });

      if (recipients.length === 0) {
        throw new Error(t('vault_ui.detail.no_active_shares'));
      }

      const encrypted = await encryptCredentialForShares(
        plaintext as CredentialPayload,
        recipients,
      );

      return credentialsApi.update(cred.id, {
        title,
        meta,
        ciphertext: encrypted.ciphertext,
        nonce: encrypted.nonce,
        dek_wrapped: encrypted.dek_wrapped,
        dek_wraps: encrypted.dek_wraps,
        // Trimis doar dacă s-a schimbat ziua — altfel serverul o păstrează.
        ...(expires !== expiryInputFromIso(cred.expires_at)
          ? { expires_at: expiryInputToIso(expires) }
          : {}),
        change_reason: changeReason.trim() || undefined,
      });
    },
    onSuccess: () => onSaved(),
    onError: (err) => {
      setError(errorMessage(err, t('vault_ui.detail.save_error')));
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError(t('vault_ui.form.title_required'));
      return;
    }
    mutation.mutate();
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="vault-detail-1" className="block text-xs font-medium text-[var(--text)] mb-1">{t('vault_ui.form.title_label')}</label>
        <input id="vault-detail-1"
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={mutation.isPending}
        />
      </div>

      <CredentialFormFields
        schema={schema}
        values={values}
        onChange={setValues}
        disabled={mutation.isPending}
        expires={expires}
        onSuggestExpiry={setExpires}
      />

      <ExpiryField value={expires} onChange={setExpires} disabled={mutation.isPending} />

      <div>
        <label htmlFor="vault-detail-2" className="block text-xs font-medium text-[var(--text)] mb-1">
          {t('vault_ui.detail.change_reason_label')}
        </label>
        <input id="vault-detail-2"
          className="input text-sm"
          value={changeReason}
          onChange={(e) => setChangeReason(e.target.value)}
          placeholder={t('vault_ui.detail.change_reason_placeholder')}
          maxLength={256}
          disabled={mutation.isPending}
        />
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2 border-t border-[var(--border)]">
        <button type="submit" className="btn-primary" disabled={mutation.isPending}>
          {mutation.isPending ? t('vault_ui.detail.saving') : t('vault_ui.detail.save_archive')}
        </button>
        <button
          type="button"
          className="btn-outline"
          onClick={onCancel}
          disabled={mutation.isPending}
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
